import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FileText, ExternalLink, Save, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const KINDS = [
  { key: "privacy", label: "Privacy Policy", path: "/legal/privacy" },
  { key: "terms", label: "Terms of Service", path: "/legal/terms" },
  { key: "shipping", label: "Shipping Policy", path: "/legal/shipping" },
  { key: "refund", label: "Refund & Returns Policy", path: "/legal/refund" },
  // Not seeded: only the store knows these details. Published once saved.
  {
    key: "about", label: "About Us", path: "/about", optional: true,
    placeholder: "<Store name> is run by <registered business / owner name>.\n\n## What we sell\n\n- <your product range>\n\n## Since\n\nServing customers since <year>.",
  },
  {
    key: "contact", label: "Contact Us", path: "/contact", optional: true,
    placeholder: "## Business\n\n- Name: <registered business name>\n- Address: <full operating address with PIN code>\n\n## Reach us\n\n- Phone: <number>\n- Email: <email>\n- Hours: <e.g. Mon-Sat, 10am-6pm>",
  },
];

/**
 * Tenant-admin editor for Privacy / Terms / Shipping / Refund pages.
 * Stored as plain text with a tiny markdown subset (`# h2`, `## h3`,
 * `- bullet`, blank line = paragraph). The same renderer lives in the
 * customer-facing `LegalPage.jsx` so what admins see in the textarea
 * here matches what visitors see on /legal/:kind.
 */
export default function AdminLegal() {
  const [pages, setPages] = useState({});      // {kind: {title, body, updated_at}}
  const [busy, setBusy] = useState({});         // {kind: "saving"}
  const [draft, setDraft] = useState({});       // {kind: {title, body}} unsaved edits
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/legal");
        if (!alive) return;
        setPages(data.pages || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load legal pages");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const current = (k) => ({
    // An unpublished About Us / Contact Us starts with its usual title.
    title: draft[k]?.title ?? pages[k]?.title ?? (KINDS.find((x) => x.key === k)?.optional ? KINDS.find((x) => x.key === k).label : ""),
    body: draft[k]?.body ?? pages[k]?.body ?? "",
  });

  const isDirty = (k) => {
    const d = draft[k];
    if (!d) return false;
    const p = pages[k] || {};
    return d.title !== p.title || d.body !== p.body;
  };

  const onChange = (k, field, value) => {
    setDraft((d) => ({
      ...d,
      [k]: { ...current(k), [field]: value },
    }));
  };

  const save = async (k) => {
    const cur = current(k);
    if (!cur.title.trim() || !cur.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setBusy((b) => ({ ...b, [k]: "saving" }));
    try {
      const { data } = await api.put(`/admin/legal/${k}`, {
        title: cur.title.trim(),
        body: cur.body,
      });
      setPages((p) => ({
        ...p,
        [k]: { title: cur.title.trim(), body: cur.body, updated_at: data.updated_at },
      }));
      setDraft((d) => {
        const n = { ...d };
        delete n[k];
        return n;
      });
      toast.success(`${KINDS.find((x) => x.key === k).label} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy((b) => ({ ...b, [k]: null }));
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-[hsl(var(--kb-muted))]" data-testid="admin-legal-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl space-y-8" data-testid="admin-legal-page">
      <div>
        <h1 className="font-display text-3xl flex items-center gap-2">
          <FileText size={22} /> Legal pages
        </h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
          These pages are linked from your storefront footer and are required for
          app-store submission. Defaults are seeded automatically for the four
          policies; customise them to match your business. About Us and Contact Us
          start empty and appear on your store once you save them — payment
          gateways (e.g. Razorpay) ask for both, with your business name, address,
          phone and email.
        </p>
        <div className="mt-3 text-xs bg-[hsl(var(--kb-surface-alt))] p-3 max-w-2xl">
          <div className="overline mb-1">Markdown shortcuts</div>
          <code className="block font-mono"># Heading</code>
          <code className="block font-mono">## Sub-heading</code>
          <code className="block font-mono">- Bullet point</code>
          <code className="block font-mono">(blank line = paragraph break)</code>
        </div>
      </div>

      {KINDS.map(({ key, label, path, optional, placeholder }) => {
        const cur = current(key);
        const dirty = isDirty(key);
        const saving = busy[key] === "saving";
        const p = pages[key] || {};
        return (
          <section
            key={key}
            className="border border-[hsl(var(--kb-border))] bg-white p-5 space-y-3"
            data-testid={`admin-legal-${key}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display text-xl">{label}</div>
                {optional && !p.updated_at && (
                  <div className="text-[10px] text-amber-700" data-testid={`admin-legal-unpublished-${key}`}>
                    Not published — not shown on your store until you save it
                  </div>
                )}
                {p.updated_at && (
                  <div className="text-[10px] text-[hsl(var(--kb-muted))]">
                    Last saved {new Date(p.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
              <Link
                to={path}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[hsl(var(--kb-primary))] hover:underline inline-flex items-center gap-1"
                data-testid={`admin-legal-preview-${key}`}
              >
                Preview <ExternalLink size={11} />
              </Link>
            </div>
            <input
              className="kb-input w-full"
              maxLength={160}
              value={cur.title}
              placeholder={optional ? label : undefined}
              onChange={(e) => onChange(key, "title", e.target.value)}
              data-testid={`admin-legal-title-${key}`}
            />
            <textarea
              className="kb-input w-full font-mono text-xs"
              rows={14}
              value={cur.body}
              placeholder={placeholder}
              onChange={(e) => onChange(key, "body", e.target.value)}
              data-testid={`admin-legal-body-${key}`}
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => save(key)}
                disabled={!dirty || saving}
                className="kb-btn-primary !py-1.5 !px-4 text-xs inline-flex items-center gap-1.5"
                data-testid={`admin-legal-save-${key}`}
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
              {dirty && (
                <span className="text-xs text-amber-700" data-testid={`admin-legal-dirty-${key}`}>
                  Unsaved changes
                </span>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
